import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import {
    archiveChatSession,
    getChannelSession,
    unarchiveChatSession,
} from 'flow/actions/channels-actions';
import SettingsListItem from 'flow/components/custom-components/SettingsListItem';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import Loading from 'flow/components/ui-components/Loading';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { ChannelKey, ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import { getIdFromEntityKey } from 'flow/utils';

const PbxLiveChatMessageSettings = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { channelKey, chatSessionKey } = useParams<{
        channelKey: ChannelKey;
        chatSessionKey: ChatSessionKey;
    }>();

    const session = useAppSelector((state) =>
        state.channels.getIn([
            'channels',
            channelKey,
            'chatSessionDTOs',
            chatSessionKey,
        ]),
    );

    const messages = useAppSelector((state) =>
        state.channels.getIn(['widgetMessages', chatSessionKey]),
    );

    useEffect(() => {
        if (!session) {
            dispatch(getChannelSession(channelKey, chatSessionKey));
        }
    }, []);

    if (!session) {
        return <Loading />;
    }

    const handleClick = () => {
        if (session.get('chatArchived')) {
            dispatch(unarchiveChatSession(channelKey, chatSessionKey));
        } else {
            dispatch(archiveChatSession(channelKey, chatSessionKey));
        }
    };

    const getDateTime = (dateTime: string) =>
        moment(dateTime).format('YYYY-MM-DD HH:mm');

    const getStatus = () => {
        if (session.get('chatArchived')) {
            const dateTime = getDateTime(
                session.getIn(['chatArchived', 'archived']),
            );
            return `${i18next.t('chat.archived')} (${dateTime})`;
        }

        return i18next.t('pbx.open');
    };

    // This will only get the date of the CURRENT loaded messages and not the whole session
    const createdDate = messages
        ? getDateTime(messages.first().get('sent'))
        : '';

    return (
        <Page
            header={i18next.t('information')}
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['pbx.channels.channelKey.chatSessionKey'],
                        {
                            channelKey,
                            chatSessionKey,
                        },
                    ),
                )
            }
        >
            <SettingsListItem
                description={`#${getIdFromEntityKey(chatSessionKey)}`}
                className="hover:bg-transparent"
            >
                {i18next.t('chat.session.number')}
            </SettingsListItem>

            <SettingsListItem
                description={createdDate}
                className="hover:bg-transparent"
            >
                {i18next.t('chat.created_on')}
            </SettingsListItem>

            <SettingsListItem
                description={getStatus()}
                className="hover:bg-transparent"
            >
                {i18next.t('status.status')}
            </SettingsListItem>

            <Divider className="my-4" />

            <SettingRow
                cta={
                    <Toggle
                        value={!!session.get('chatArchived')}
                        onChange={handleClick}
                        text={i18next.t('chat.archive_this_chat')}
                    />
                }
            >
                <Icon iconKey="archive" />
                <p className="text-base/5 first-letter:uppercase">
                    {i18next.t('chat.archive_this_chat')}
                </p>
            </SettingRow>
        </Page>
    );
};

export default PbxLiveChatMessageSettings;
